/**
 * Payor router (US-028).
 *
 * Provides typeahead, list, get, create, update, setAffiliation, revalidate.
 * Validation runs inline (synchronously) on create + revalidate via the
 * `@app/payor-validation` provider abstraction. The abstraction guarantees
 * no exception escapes — adapter failures collapse to `unable_to_verify`
 * cleanly so a flaky provider never blocks an athlete.
 *
 * Backfill of legacy `Deal.brandName/brandContact/brandEmail` into a Payor
 * row is intentionally not done here — it's a separate data migration to
 * keep this PR additive.
 *
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import {
  normalizePayorMatchKey,
  payorAffiliationEnum,
  payorCreateSchema,
  payorUpdateSchema,
} from '@app/validators';
import {
  getPayorValidationProvider,
  validatePayorSafely,
  type PayorValidationInput,
} from '@app/payor-validation';
import { z } from 'zod';
import { protectedProcedure } from '../middleware/auth';
import { NIL_GO_THRESHOLD_USD } from '../services/nil-go-readiness';
import { computePayorAggregateExposure } from '../services/payor-aggregation';

// Normalize a free-form input value to the canonical storage form.
// Empty strings collapse to undefined so we never persist '' (which would
// poison email/url validation downstream).
function blankToUndefined<T extends string | undefined | null>(v: T): string | undefined {
  if (v === null || v === undefined) return undefined;
  const trimmed = v.trim();
  return trimmed === '' ? undefined : trimmed;
}

function normalizeEin(v: string | undefined | null): string | undefined {
  const digits = (v ?? '').replace(/\D/g, '');
  return digits.length === 9 ? digits : undefined;
}

// biome-ignore lint/suspicious/noExplicitAny: prisma types are too narrow here
function parsePayor(p: any) {
  if (!p) return null;
  return {
    ...p,
    validationDetails: p.validationDetails ? safeParseJson(p.validationDetails) : null,
  };
}

function safeParseJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

async function runValidation(input: PayorValidationInput) {
  const provider = getPayorValidationProvider();
  return validatePayorSafely(input, provider);
}

const listInputSchema = z.object({
  search: z.string().optional(),
  validationStatus: z
    .enum(['unverified', 'verified', 'unable_to_verify', 'flagged'])
    .optional(),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(50),
});

const searchInputSchema = z.object({
  query: z.string().min(1).max(200),
  limit: z.number().int().positive().max(20).default(8),
});

export const payorRouter = {
  /**
   * List payors visible to the athlete: their own + org-shared rows at any
   * org they're a member of. Indexed lookup; supports a free-text search
   * across legalName/displayName/contactEmail.
   */
  list: protectedProcedure.input(listInputSchema).handler(async ({ context, input }) => {
    const { db, session } = context;
    try {
      const userId = session.user.id;

      // Resolve org membership ids once — used as an OR clause below.
      const memberships = await db.member.findMany({
        where: { userId },
        select: { organizationId: true },
      });
      const orgIds = memberships.map((m: { organizationId: string }) => m.organizationId);

      const visibleOr: Record<string, unknown>[] = [{ ownerId: userId }];
      if (orgIds.length > 0) {
        visibleOr.push({ organizationId: { in: orgIds } });
      }
      const where: Record<string, unknown> = { OR: visibleOr };

      if (input.validationStatus) {
        where.validationStatus = input.validationStatus;
      }

      if (input.search) {
        const term = input.search;
        // AND together with the visibility OR so we don't accidentally widen.
        where.AND = [
          { OR: visibleOr },
          {
            OR: [
              { legalName: { contains: term, mode: 'insensitive' } },
              { displayName: { contains: term, mode: 'insensitive' } },
              { contactEmail: { contains: term, mode: 'insensitive' } },
            ],
          },
        ];
        delete where.OR;
      }

      const [rows, total] = await Promise.all([
        db.payor.findMany({
          where,
          orderBy: { legalName: 'asc' },
          skip: (input.page - 1) * input.limit,
          take: input.limit,
        }),
        db.payor.count({ where }),
      ]);

      return {
        payors: rows.map(parsePayor),
        total,
        page: input.page,
        limit: input.limit,
      };
    } catch (err) {
      if (err instanceof ORPCError) throw err;
      const message = err instanceof Error ? err.message : String(err);
      throw new ORPCError('INTERNAL_SERVER_ERROR', { message: `Payor list failed: ${message}` });
    }
  }),

  /**
   * Typeahead search used by the deal create form. Always returns "your own"
   * payors first so the athlete's recent entries surface fastest. Matching
   * is case- and whitespace-insensitive (see `normalizePayorMatchKey`).
   */
  search: protectedProcedure
    .input(searchInputSchema)
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const userId = session.user.id;
      const memberships = await db.member.findMany({
        where: { userId },
        select: { organizationId: true },
      });
      const orgIds = memberships.map((m: { organizationId: string }) => m.organizationId);

      const visibleOr: Record<string, unknown>[] = [{ ownerId: userId }];
      if (orgIds.length > 0) {
        visibleOr.push({ organizationId: { in: orgIds } });
      }

      // We could do the normalized match in SQL with a generated column,
      // but volume is tiny per-user — fetch a wider set + filter in JS.
      const candidates = await db.payor.findMany({
        where: {
          OR: visibleOr,
        },
        orderBy: { updatedAt: 'desc' },
        take: 100,
      });

      const needle = normalizePayorMatchKey(input.query);
      const matches = candidates
        .map(parsePayor)
        // biome-ignore lint/suspicious/noExplicitAny: parsePayor returns any
        .filter((p: any) => {
          if (!p) return false;
          const fields = [p.legalName, p.displayName, p.contactEmail]
            .filter(Boolean)
            .map((s: string) => normalizePayorMatchKey(s));
          return fields.some((f: string) => f.includes(needle));
        })
        .slice(0, input.limit);

      return { matches };
    }),

  get: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db } = context;
      const row = await db.payor.findUnique({ where: { id: input.id } });
      if (!row) {
        throw new ORPCError('NOT_FOUND', { message: 'Payor not found' });
      }
      return parsePayor(row);
    }),

  /**
   * Create a new payor for the signed-in athlete. The router enforces
   * `ownerId = session user`; client-supplied `ownerId` is ignored.
   *
   * Validation runs synchronously here so the response includes the badge
   * state — fine because the safe wrapper guarantees a bounded result and
   * the mock adapter returns in <1ms. A real Middesk adapter call returns
   * in ~200ms on the happy path which is still acceptable inline; if it
   * gets slower, move to an Inngest job (deferred follow-up).
   */
  create: protectedProcedure
    .input(payorCreateSchema)
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const userId = session.user.id;

      const legalName = input.legalName.trim();
      const ein = normalizeEin(input.ein);

      const validation = await runValidation({
        legalName,
        ein: ein ?? null,
        website: blankToUndefined(input.website) ?? null,
        addressLine1: blankToUndefined(input.addressLine1) ?? null,
        city: blankToUndefined(input.city) ?? null,
        state: blankToUndefined(input.state) ?? null,
        postalCode: blankToUndefined(input.postalCode) ?? null,
      });

      const { createId } = await import('@app/db');
      const row = await db.payor.create({
        data: {
          id: createId(),
          legalName,
          displayName: blankToUndefined(input.displayName),
          contactName: blankToUndefined(input.contactName),
          contactEmail: blankToUndefined(input.contactEmail),
          contactPhone: blankToUndefined(input.contactPhone),
          website: blankToUndefined(input.website),
          ein,
          addressLine1: blankToUndefined(input.addressLine1),
          addressLine2: blankToUndefined(input.addressLine2),
          city: blankToUndefined(input.city),
          state: blankToUndefined(input.state),
          postalCode: blankToUndefined(input.postalCode),
          country: input.country ?? 'US',
          industry: blankToUndefined(input.industry),
          affiliation: input.affiliation,
          affiliationSetBy: input.affiliation !== 'unknown' ? userId : null,
          affiliationSetAt: input.affiliation !== 'unknown' ? new Date() : null,
          validationStatus: validation.status,
          validationProvider: validation.provider,
          validatedAt: new Date(),
          validationDetails: JSON.stringify(validation.details),
          ownerId: userId,
          organizationId: input.organizationId ?? null,
        },
      });

      return parsePayor(row);
    }),

  update: protectedProcedure
    .input(payorUpdateSchema)
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const existing = await db.payor.findUnique({ where: { id: input.id } });
      if (!existing) {
        throw new ORPCError('NOT_FOUND', { message: 'Payor not found' });
      }
      if (existing.ownerId !== session.user.id && session.user.role !== 'admin') {
        throw new ORPCError('FORBIDDEN', { message: 'Not your payor' });
      }

      const patch: Record<string, unknown> = {};
      if (input.legalName !== undefined) patch.legalName = input.legalName.trim();
      if (input.displayName !== undefined) patch.displayName = blankToUndefined(input.displayName);
      if (input.contactName !== undefined) patch.contactName = blankToUndefined(input.contactName);
      if (input.contactEmail !== undefined) patch.contactEmail = blankToUndefined(input.contactEmail);
      if (input.contactPhone !== undefined) patch.contactPhone = blankToUndefined(input.contactPhone);
      if (input.website !== undefined) patch.website = blankToUndefined(input.website);
      if (input.ein !== undefined) patch.ein = normalizeEin(input.ein);
      if (input.addressLine1 !== undefined) patch.addressLine1 = blankToUndefined(input.addressLine1);
      if (input.addressLine2 !== undefined) patch.addressLine2 = blankToUndefined(input.addressLine2);
      if (input.city !== undefined) patch.city = blankToUndefined(input.city);
      if (input.state !== undefined) patch.state = blankToUndefined(input.state);
      if (input.postalCode !== undefined) patch.postalCode = blankToUndefined(input.postalCode);
      if (input.country !== undefined) patch.country = input.country;
      if (input.industry !== undefined) patch.industry = blankToUndefined(input.industry);

      const updated = await db.payor.update({ where: { id: input.id }, data: patch });

      // Write-through: every Deal that links to this payor stores a
      // denormalized `brandName` / `brandContact` / `brandEmail` (legacy
      // columns kept for back-compat readers). When the payor's canonical
      // fields change, sync them onto every attached deal so the deal
      // header, deals-list rows, and NIL Go payload don't lag until the
      // athlete re-saves the deal manually. Only sync fields the payor
      // edit actually changed.
      const dealPatch: Record<string, unknown> = {};
      if (patch.legalName !== undefined) dealPatch.brandName = patch.legalName;
      if (patch.contactName !== undefined) dealPatch.brandContact = patch.contactName;
      if (patch.contactEmail !== undefined) dealPatch.brandEmail = patch.contactEmail;
      if (Object.keys(dealPatch).length > 0) {
        await db.deal.updateMany({ where: { payorId: input.id }, data: dealPatch });
      }

      return parsePayor(updated);
    }),

  /** Explicit affiliation classifier — kept separate so the audit trail is clean. */
  setAffiliation: protectedProcedure
    .input(z.object({ id: z.string(), affiliation: payorAffiliationEnum }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const existing = await db.payor.findUnique({ where: { id: input.id } });
      if (!existing) throw new ORPCError('NOT_FOUND', { message: 'Payor not found' });
      if (existing.ownerId !== session.user.id && session.user.role !== 'admin') {
        throw new ORPCError('FORBIDDEN', { message: 'Not your payor' });
      }
      const updated = await db.payor.update({
        where: { id: input.id },
        data: {
          affiliation: input.affiliation,
          affiliationSetBy: session.user.id,
          affiliationSetAt: new Date(),
        },
      });
      return parsePayor(updated);
    }),

  /** Re-run validation on demand. Owner only. */
  revalidate: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const existing = await db.payor.findUnique({ where: { id: input.id } });
      if (!existing) throw new ORPCError('NOT_FOUND', { message: 'Payor not found' });
      if (existing.ownerId !== session.user.id && session.user.role !== 'admin') {
        throw new ORPCError('FORBIDDEN', { message: 'Not your payor' });
      }
      const result = await runValidation({
        legalName: existing.legalName,
        ein: existing.ein ?? null,
        website: existing.website ?? null,
        addressLine1: existing.addressLine1 ?? null,
        city: existing.city ?? null,
        state: existing.state ?? null,
        postalCode: existing.postalCode ?? null,
      });
      const updated = await db.payor.update({
        where: { id: input.id },
        data: {
          validationStatus: result.status,
          validationProvider: result.provider,
          validatedAt: new Date(),
          validationDetails: JSON.stringify(result.details),
        },
      });
      return parsePayor(updated);
    }),

  /**
   * US-033.3 — YTD aggregate exposure for the signed-in athlete with this
   * payor in the current tax year. Powers the payor detail page's $600
   * progress bar AND the over-threshold banner on the NIL Go readiness
   * panel (when this number crosses the threshold even though a single
   * deal might not).
   */
  ytdExposure: protectedProcedure
    .input(z.object({ payorId: z.string(), taxYear: z.number().int().optional() }))
    .handler(async ({ context, input }) => {
      const exposure = await computePayorAggregateExposure({
        db: context.db,
        athleteId: context.session.user.id,
        payorId: input.payorId,
        taxYear: input.taxYear,
      });
      return {
        total: exposure.total,
        taxYear: exposure.taxYear,
        thresholdUsd: NIL_GO_THRESHOLD_USD,
        overThreshold: exposure.total >= NIL_GO_THRESHOLD_USD,
        deals: exposure.deals,
      };
    }),
};
